import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import api from '../../services/api';
import { toast } from 'sonner';
import { useBusinessTimezone } from '../../utils/businessTime';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ScrollText,
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Clock,
  User,
  FileText,
  Activity,
  BarChart3,
  X
} from 'lucide-react';

const AuditLogs = () => {
  const businessTz = useBusinessTimezone();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    module: '',
    action: '',
    user_role: '',
    search: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter options
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  
  // Detail dialog
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      
      if (filters.module) params.append('module', filters.module);
      if (filters.action) params.append('action', filters.action);
      if (filters.user_role) params.append('user_role', filters.user_role);
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await api.get(`/audit?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  const fetchFilterOptions = async () => {
    try {
      const [modulesRes, actionsRes] = await Promise.all([
        api.get('/audit/modules'),
        api.get('/audit/actions')
      ]);
      setModules(modulesRes.data.modules || []);
      setActions(actionsRes.data.actions || []);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/audit/stats?days=7');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchFilterOptions();
    fetchStats();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      module: '',
      action: '',
      user_role: '',
      search: '',
      start_date: '',
      end_date: ''
    });
    setPage(1);
  };

  const getActionBadge = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-700',
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700',
      login: 'bg-purple-100 text-purple-700',
      logout: 'bg-gray-100 text-gray-700',
      approve: 'bg-emerald-100 text-emerald-700',
      reject: 'bg-orange-100 text-orange-700',
      toggle_feature: 'bg-amber-100 text-amber-700'
    };
    return <Badge className={`${colors[action] || 'bg-slate-100 text-slate-700'} capitalize`}>{action}</Badge>;
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700',
      coach: 'bg-blue-100 text-blue-700',
      staff: 'bg-green-100 text-green-700',
      parent: 'bg-purple-100 text-purple-700',
      customer: 'bg-amber-100 text-amber-700'
    };
    return <Badge className={`${colors[role] || 'bg-slate-100 text-slate-700'} capitalize`}>{role}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', { timeZone: businessTz });
  };

  const viewLogDetail = (log) => {
    setSelectedLog(log);
    setShowDetail(true);
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <DashboardLayout title="Audit Logs">
      <div className="p-6 space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ScrollText className="h-6 w-6" />
            Audit Logs
          </h1>
          <p className="text-slate-500 mt-1">Track all create, edit, and delete actions across the system</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? 'border-blue-500 text-blue-600' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v).length})`}
          </Button>
          <Button variant="outline" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Last 7 Days</p>
                  <p className="text-2xl font-bold">{stats.total_logs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Top Module</p>
                  <p className="text-lg font-semibold capitalize">
                    {stats.by_module?.[0]?.module || '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Top Action</p>
                  <p className="text-lg font-semibold capitalize">
                    {stats.by_action?.[0]?.action || '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <User className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Most Active User</p>
                  <p className="text-lg font-semibold truncate max-w-[150px]" title={stats.by_user?.[0]?.user_name}>
                    {stats.by_user?.[0]?.user_name || '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
                  <Input
                    placeholder="Name, email, ID..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-8 text-sm"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Module</Label>
                <Select
                  value={filters.module || 'all'}
                  onValueChange={(v) => handleFilterChange('module', v === 'all' ? '' : v)}
                >
                  <SelectTrigger data-testid="module-filter">
                    <SelectValue placeholder="All modules" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All modules</SelectItem>
                    {modules.map(m => (
                      <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Action</Label>
                <Select
                  value={filters.action || 'all'}
                  onValueChange={(v) => handleFilterChange('action', v === 'all' ? '' : v)}
                >
                  <SelectTrigger data-testid="action-filter">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    {actions.map(a => (
                      <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">User Role</Label>
                <Select
                  value={filters.user_role || 'all'}
                  onValueChange={(v) => handleFilterChange('user_role', v === 'all' ? '' : v)}
                >
                  <SelectTrigger data-testid="role-filter">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  className="text-sm"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>
            
            {hasActiveFilters && (
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logs Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Activity Log</CardTitle>
            <p className="text-sm text-slate-500">{total} total records</p>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
            </div>
          ) : logs.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Module</TableHead>
                    <TableHead>Record</TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm text-slate-600">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(log.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{log.user_name}</p>
                          <p className="text-xs text-slate-500">{log.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(log.user_role)}</TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell className="capitalize text-sm">{log.module}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm truncate max-w-[200px]" title={log.record_name}>
                            {log.record_name || '-'}
                          </p>
                          <p className="text-xs text-slate-400 font-mono">{log.record_id?.substring(0, 8)}...</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewLogDetail(log)}
                          data-testid={`view-log-${log.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <ScrollText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No audit logs found</p>
              <p className="text-sm mt-1">Activity will be recorded here as users interact with the system</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5" />
              Audit Log Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Timestamp</Label>
                  <p className="font-medium">{formatDate(selectedLog.timestamp)}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">User</Label>
                  <p className="font-medium">{selectedLog.user_name}</p>
                  <p className="text-xs text-slate-500">{selectedLog.user_email}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Role</Label>
                  {getRoleBadge(selectedLog.user_role)}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Action</Label>
                  {getActionBadge(selectedLog.action)}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Module</Label>
                  <p className="font-medium capitalize">{selectedLog.module}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Record Type</Label>
                  <p className="font-medium capitalize">{selectedLog.record_type}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Record Name</Label>
                <p className="font-medium">{selectedLog.record_name || '-'}</p>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Record ID</Label>
                <p className="font-mono text-sm bg-slate-100 p-2 rounded">{selectedLog.record_id || '-'}</p>
              </div>
              
              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Changes</Label>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    {Object.entries(selectedLog.changes).map(([field, change]) => (
                      <div key={field} className="flex items-start gap-2 text-sm">
                        <span className="font-medium min-w-[100px]">{field}:</span>
                        <span className="text-red-600 line-through">{JSON.stringify(change.old)}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-green-600">{JSON.stringify(change.new)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Additional Info</Label>
                  <pre className="bg-slate-100 p-3 rounded text-xs overflow-auto max-h-[200px]">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
              
              {selectedLog.ip_address && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">IP Address</Label>
                  <p className="font-mono text-sm">{selectedLog.ip_address}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogs;
